import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Stack, Text, useViewport } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import Container from "../Container/index.js";
import { GeniusAirlinesBanner, GeniusAirlinesBannerMobile } from "../GeniusAirlinesBanner/index.js";
import SpringDealBanner from "../SpringDealBanner/index.js";
import SummerDealBanner from "../SummerDealBanner/index.js";
import { mcn } from "../../../utils/mergeClassnames.js";
import styles from "./HomepageOffers.module.css";
import { getIndexCampaigns } from "../../../store/indexCampaigns/selectors.js";
import useProfiler from "../../../hooks/useProfiler.js";
import { useSpringDeal } from "../../../hooks/useSpringDeal.js";
import { useSummerDeal } from "../../../hooks/useSummerDeal.js";
import web_flights_lve_deal_landing_page from "../../../utils/experiments/lve/web_flights_lve_deal_landing_page.js";
const HomepageOffers = ({ isMobile }) => {
    const { isLarge } = useViewport();
    const { isLoggedIn, isLoggedOut } = useProfiler();
    const { geniusAirlines: geniusAirlineData, dealsLandingPageVariant = 0, dealsLandingPageEligible = false } = useSelector(getIndexCampaigns);
    const i18n = useI18n();
    const { isSpringDealExp } = useSpringDeal();
    const showSpringDealBanner = isSpringDealExp && isMobile;
    const { isSummerDealExp } = useSummerDeal();
    const showSummerDealBanner = isSummerDealExp && isMobile;
    const geniusStyles = mcn(isLarge ? styles.item : styles.itemNoWidth, styles.itemSingle);
    const isUserLoggedIn = isLoggedIn();
    const isUserLoggedOut = isLoggedOut();
    const showDealsEntryPoint = useMemo(() => dealsLandingPageEligible && dealsLandingPageVariant > 0, [dealsLandingPageEligible, dealsLandingPageVariant]);
    const showGeniusBanner = !!geniusAirlineData?.airlines && (isUserLoggedIn || showDealsEntryPoint);
    const renderGeniusBanner = !showSpringDealBanner && !showSummerDealBanner && showGeniusBanner;
    useEffect(() => {
        if (showSpringDealBanner || showSummerDealBanner)
            return;
        if (!dealsLandingPageEligible)
            return;
        web_flights_lve_deal_landing_page.stages.main();
        if (isMobile) {
            web_flights_lve_deal_landing_page.stages.mdot_home();
        }
        else {
            web_flights_lve_deal_landing_page.stages.desktop_home();
        }
        if (isUserLoggedOut)
            web_flights_lve_deal_landing_page.stages.logged_out_home();
        if (isUserLoggedIn)
            web_flights_lve_deal_landing_page.stages.logged_in_home();
    }, [dealsLandingPageEligible, isMobile, isUserLoggedIn, isUserLoggedOut, showSpringDealBanner, showSummerDealBanner]);
    useEffect(() => {
        if (!dealsLandingPageEligible || !renderGeniusBanner || !isUserLoggedIn)
            return;
        web_flights_lve_deal_landing_page.stages.logged_in_banner_visible();
        if (isMobile) {
            web_flights_lve_deal_landing_page.stages.mdot_logged_in_banner_visible();
        }
        else {
            web_flights_lve_deal_landing_page.stages.desktop_logged_in_banner_visible();
        }
    }, [dealsLandingPageEligible, isMobile, renderGeniusBanner, isUserLoggedIn]);
    if (!showGeniusBanner && !showSpringDealBanner && !showSummerDealBanner)
        return null;
    return (React.createElement(Container, { className: isMobile ? styles.mobileContainer : styles.container },
        showGeniusBanner || showSpringDealBanner || showSummerDealBanner ? (React.createElement(Stack, { direction: "column", gap: 0, className: styles.top },
            React.createElement(Text, { variant: "headline_2", tagName: "h2" }, i18n.trans(t("flights_index_offers_carousel_header"))),
            React.createElement(Text, { variant: "body_1", color: "neutral_alt" }, i18n.trans(t("flights_index_offers_carousel_subheader"))))) : null,
        React.createElement(Stack, { direction: !isLarge ? "column" : "row", alignItems: "stretch", justifyContent: "center", gap: 4, wrap: "wrap" },
            showSummerDealBanner ? (React.createElement(Stack.Item, { className: styles.itemNoWidth },
                React.createElement(SummerDealBanner, null))) : showSpringDealBanner ? (React.createElement(Stack.Item, { className: styles.itemNoWidth },
                React.createElement(SpringDealBanner, null))) : null,
            renderGeniusBanner ? (React.createElement(Stack.Item, { className: geniusStyles }, isMobile ? (React.createElement(GeniusAirlinesBannerMobile, { geniusAirlineBannerData: geniusAirlineData, showDealsEntryPoint: showDealsEntryPoint })) : (React.createElement(GeniusAirlinesBanner, { geniusAirlineBannerData: geniusAirlineData, isSecondaryOffer: false, showDealsEntryPoint: showDealsEntryPoint })))) : null)));
};
export default HomepageOffers;
